import { useRef, useState } from "react";
import { CellWrap } from "@/components/board/cells/CellWrap";
import { Icon } from "@/components/ui/Icon";
import { FileViewer } from "@/components/ui/FileViewer";
import { api } from "@/lib/api";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import { ACCEPT_ATTR, MAX_FILE_BYTES, isAcceptedFile, isImageRef } from "@/lib/fileEmbed";
import type { FileRef } from "@/lib/types";

interface FileCellProps {
  value?: FileRef[];
  rowH?: number;
  /** Omitted => read-only: chips still open, but nothing can be added or removed. */
  onPatch?: (value: FileRef[]) => void;
  /** Detail panel stacks and wraps; the table row is a single fixed-height line. */
  variant?: "cell" | "detail";
}

// How many chips a table cell shows before collapsing the rest into "+N".
// Mirrors TagsCell's slice(0, 3) so the two dense cells agree.
const INLINE_MAX = 3;

export function FileCell({ value, rowH = 44, onPatch, variant = "cell" }: FileCellProps) {
  const refs = Array.isArray(value) ? value : [];
  const inputRef = useRef<HTMLInputElement>(null);
  const [view, setView] = useState<{ url: string; name: string } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(0);
  const editable = Boolean(onPatch);
  const isDetail = variant === "detail";

  async function upload(files: File[]) {
    if (!onPatch || files.length === 0) return;
    const accepted: File[] = [];
    for (const f of files) {
      if (!isAcceptedFile(f)) {
        showToast(t.commentFileType, "error");
        continue;
      }
      if (f.size > MAX_FILE_BYTES) {
        showToast(t.commentFileTooLarge, "error");
        continue;
      }
      accepted.push(f);
    }
    if (accepted.length === 0) return;

    setBusy((n) => n + accepted.length);
    const uploaded: FileRef[] = [];
    for (const f of accepted) {
      try {
        uploaded.push(await api.uploadFile(f));
      } catch {
        showToast(t.commentFileFailed, "error");
      } finally {
        setBusy((n) => n - 1);
      }
    }
    // Append to whatever is on the cell NOW, not to the render-time snapshot —
    // two uploads landing in the same tick must not clobber each other.
    if (uploaded.length > 0) onPatch([...refs, ...uploaded]);
  }

  function remove(key: string) {
    onPatch?.(refs.filter((r) => r.key !== key));
  }

  const shown = isDetail ? refs : refs.slice(0, INLINE_MAX);
  const overflow = refs.length - shown.length;

  return (
    <>
      <CellWrap
        rowH={isDetail ? undefined : rowH}
        {...(editable && refs.length === 0 ? { onClick: () => inputRef.current?.click() } : {})}
        style={{
          gap: 6,
          flexWrap: isDetail ? "wrap" : "nowrap",
          overflow: "hidden",
          ...(isDetail ? { height: "auto", minHeight: 40, padding: "6px 12px" } : {}),
          ...(dragOver ? { outline: "2px dashed var(--accent)", outlineOffset: -3 } : {}),
        }}
        {...(editable
          ? {
              onDragOver: (e: React.DragEvent) => {
                if (!Array.from(e.dataTransfer.types).includes("Files")) return;
                e.preventDefault();
                if (!dragOver) setDragOver(true);
              },
              onDragLeave: (e: React.DragEvent) => {
                if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                setDragOver(false);
              },
              onDrop: (e: React.DragEvent) => {
                e.preventDefault();
                setDragOver(false);
                void upload(Array.from(e.dataTransfer.files));
              },
            }
          : {})}
      >
        {refs.length === 0 ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12.5,
              color: "var(--ink-4)",
            }}
          >
            <Icon name="file" size={13} /> {busy > 0 ? t.commentFileUploading : t.attach}
          </span>
        ) : (
          <>
            {shown.map((r) => (
              <Chip
                key={r.key}
                fileRef={r}
                onOpen={() => setView({ url: r.url, name: r.name })}
                {...(editable ? { onRemove: () => remove(r.key) } : {})}
              />
            ))}
            {overflow > 0 ? (
              <span style={{ fontSize: 11.5, color: "var(--ink-4)", flexShrink: 0 }}>
                +{overflow}
              </span>
            ) : null}
            {busy > 0 ? (
              <span style={{ fontSize: 11.5, color: "var(--ink-4)", flexShrink: 0 }}>
                {t.commentFileUploading}
              </span>
            ) : null}
            {editable ? (
              <button
                type="button"
                title={t.attach}
                aria-label={t.attach}
                onClick={(e) => {
                  e.stopPropagation();
                  inputRef.current?.click();
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                  // "Add another" is not part of the last chip. Extra room so
                  // the two adjacent controls — remove THIS file vs add ANOTHER
                  // — can't be confused or mis-tapped for one another.
                  marginLeft: 4,
                  borderRadius: 6,
                  border: "1px dashed var(--hairline-strong)",
                  background: "transparent",
                  color: "var(--ink-4)",
                  cursor: "pointer",
                }}
              >
                <Icon name="plus" size={12} />
              </button>
            ) : null}
          </>
        )}
      </CellWrap>

      {editable ? (
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTR}
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            void upload(Array.from(e.target.files ?? []));
            e.target.value = "";
          }}
        />
      ) : null}

      {view ? <FileViewer url={view.url} name={view.name} onClose={() => setView(null)} /> : null}
    </>
  );
}

function Chip({
  fileRef,
  onOpen,
  onRemove,
}: {
  fileRef: FileRef;
  onOpen: () => void;
  onRemove?: () => void;
}) {
  const image = isImageRef(fileRef);
  return (
    // The chip is [content | control], split by a full-height hairline. The X
    // used to sit flush against the truncated filename with a 5px gap, so the
    // ellipsis ran straight into it and it read as part of the name. The rule
    // gives the destructive control its own compartment — structure, not
    // decoration. `stretch` (not `center`) is what lets the divider and the hit
    // area span the chip's whole height.
    <span
      style={{
        display: "inline-flex",
        alignItems: "stretch",
        flexShrink: 0,
        maxWidth: 180,
        minHeight: 24,
        borderRadius: 6,
        border: "1px solid var(--hairline)",
        background: "var(--surface)",
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        title={fileRef.name}
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          minWidth: 0,
          padding: image ? 0 : "0 9px",
          border: "none",
          background: "transparent",
          cursor: "pointer",
          color: "var(--accent)",
          fontSize: 12,
          fontWeight: 500,
          fontFamily: "inherit",
        }}
      >
        {image ? (
          <img
            src={fileRef.url}
            alt={fileRef.name}
            style={{ width: 26, height: 26, objectFit: "cover", borderRadius: 5, display: "block" }}
          />
        ) : (
          <>
            <Icon name="file" size={12} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {fileRef.name}
            </span>
          </>
        )}
      </button>
      {onRemove ? (
        <>
          <span aria-hidden style={{ width: 1, background: "var(--hairline)", flexShrink: 0 }} />
          <button
            type="button"
            title={t.commentFileRemove}
            aria-label={`${t.commentFileRemove}: ${fileRef.name}`}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--surface-soft)";
              e.currentTarget.style.color = "var(--danger)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--ink-4)";
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              // 22px wide and full-height: a real touch target on the iPad,
              // where the old 14px square was a coin toss. The GLYPH stays
              // small — touch needs area, not a bigger X.
              width: 22,
              flexShrink: 0,
              alignSelf: "stretch",
              border: "none",
              background: "transparent",
              color: "var(--ink-4)",
              cursor: "pointer",
              padding: 0,
              transition: "background .12s, color .12s",
            }}
          >
            <Icon name="x" size={11} />
          </button>
        </>
      ) : null}
    </span>
  );
}
